import styled from 'styled-components';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { Icon } from '@elfsight/lib-icon';
import { Tooltip } from '@elfsight/widget-modal';
import { formatAbbrNumber } from '@elfsight/widget-utils';
import { useTranslation } from 'react-i18next';
import type { CounterBlock } from '../../../constants';
import { counters } from '../../../constants';
import { Link } from '../../lib/components';
import { useSocialFeedSettings } from '../../utils';
import { usePost } from '../Post';

interface CountersProps {
  color: string;
}

type MappedCounterType = CounterBlock & {
  value: number;
};

export function Counters({ color, ...forwardingProps }: CountersProps) {
  const { t } = useTranslation();
  const { id, url, counters: countersData = {} } = usePost();
  const {
    enableExternalLinks,
    language,
    tooltipBackgroundColor,
    tooltipTextColor,
    googleAnalytics = [],
    widgetId = '',
    editMode,
    font
  } = useSocialFeedSettings();

  const handleCounterClick = () => {
    sendGoogleAnalyticsEvents({
      eventType: 'socialPlatformOpened',
      googleAnalyticsEvents: googleAnalytics,
      editMode,
      valueMapping: {
        widgetId,
        postId: id,
        postUrl: url || '',
        entryPoint: 'post'
      }
    });
  };

  const countersEntries = Object.entries(counters);

  const mappedCounters: MappedCounterType[] = countersEntries
    .map(([key, counter]) => {
      if (countersData[key] === undefined) {
        return;
      }

      return {
        ...counter,
        value: countersData[key]
      };
    })
    .filter(Boolean) as MappedCounterType[];

  if (mappedCounters.length === 0) {
    return null;
  }

  return (
    <Container _textColor={color} {...forwardingProps}>
      {mappedCounters.map(({ value, title, icon }) => {
        const translatedTitle = t(title);

        return (
          <Tooltip
            key={title}
            title={translatedTitle}
            background={tooltipBackgroundColor}
            fontColor={tooltipTextColor}
            font={font}
          >
            <Counter
              href={url}
              hoverEffect="none"
              aria-label={translatedTitle}
              _enableExternalLinks={enableExternalLinks}
              onClick={handleCounterClick}
            >
              {!!icon && (
                <CounterIcon component={icon} fill={color} size={16} />
              )}

              <span>
                <Title _isTitleShown={!!value}>
                  {formatAbbrNumber(value, language)}
                </Title>
              </span>
            </Counter>
          </Tooltip>
        );
      })}
    </Container>
  );
}

const CounterIcon = styled(Icon)``;

const Title = styled.div<{
  _isTitleShown: boolean;
}>`
  display: ${({ _isTitleShown }) => (_isTitleShown ? 'block' : 'none')};
`;

const Counter = styled(Link)<{
  _enableExternalLinks: boolean;
}>`
  display: flex;
  opacity: 0.7;
  cursor: ${({ _enableExternalLinks }) =>
    _enableExternalLinks ? 'pointer' : 'default'};

  &:hover {
    opacity: ${({ _enableExternalLinks }) => (_enableExternalLinks ? 1 : 0.7)};
  }

  & ${CounterIcon} {
    margin-right: 4px;
  }
`;

const Container = styled.div<{
  _textColor: string;
}>`
  display: flex;
  font-size: 13px;
  color: ${({ _textColor }) => _textColor};

  & ${Counter}:not(:first-child) {
    padding-left: 16px;
  }
`;
